import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './matchInteraction.scss';
import AlternativeCell from './AlternativeCell';
import InteractionNumber from './InteractionNumber';
import scorableItemNumberingService from '../../../../services/questionView/ScorableItemNumberingService';
import settingsReader from '../../../../store/readHelpers/SettingsReader';
import matchInteractionA11yService from '../../../../services/matchInteractionA11yService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';

const MatchInteractionRow = (props) => {
    const {
        correctResponses,
        handleUserResponse,
        isInverted,
        isReadOnly,
        modelIdentifier,
        rowChoice,
        flaggedItems,
        questionModel,
        userResponse,
        matchColumns,
    } = props;

    const rowChoiceIdentifier = rowChoice.identifier;
    const [flaggedItemCount, setFlaggedItemCount] = useState(null);

    const isVisited = (flaggedItems?.length === flaggedItemCount);

    const shouldFocusBeMovedToNextRow = (currentRow, direction) => {
        const rows = Array.from(currentRow.parentNode.children);
        const currentIndex = rows.indexOf(currentRow);
        const nextIndex = currentIndex + direction;
        if (nextIndex >= 0 && nextIndex < rows.length) {
            const nextRow = rows[nextIndex];
            const focusables = settingsReader.getFocusableElements(nextRow);
            if (focusables.length > 0) {
                const target = direction === -1 ? focusables[focusables.length - 1] : focusables[0];
                target.focus();
                return true;
            }
        }
        return false;
    };

    const jumpToNextRow = (currentRow, direction = 1) => {
        const rows = Array.from(currentRow.parentNode.children);
        const currentIndex = rows.indexOf(currentRow);
        const nextRow = rows[currentIndex + 1];

        if (nextRow) {
            const nextFocusables = settingsReader.getFocusableElements(nextRow);
            if (nextFocusables.length > 0) {
                nextFocusables[0].focus();
                return;
            }
        }
        settingsReader.getFocusOutsideTable(direction);
    };

    const handleTab = (e, currentRow) => {
        // Try enhanced accessibility navigation first
        if (matchInteractionA11yService.handleEnhancedTabNavigation(
            e,
            currentRow,
            setFlaggedItemCount,
            flaggedItems,
            isFlaggingEnabled,
            isVisited
        )) {
            return;
        }

        // Fallback to original behavior
        e.preventDefault();
        setFlaggedItemCount(flaggedItems.length);

        const focusables = settingsReader.getFocusableElements(currentRow);
        const active = document.activeElement;
        const currentIndex = focusables.indexOf(active);
        const direction = e.shiftKey ? -1 : 1;
        const nextIndex = currentIndex + direction;
        const isFlagFocused = active && active.tagName.toLowerCase() === 'button';

        const isFlaggedEarlyJump = direction === 1
                && isFlaggingEnabled
                && isFlagFocused
                && !isVisited;

        if (isFlaggedEarlyJump) {
            jumpToNextRow(currentRow, direction);
            return;
        }

        if (nextIndex >= 0 && nextIndex < focusables.length) {
            focusables[nextIndex].focus();
            return;
        }

        if (!shouldFocusBeMovedToNextRow(currentRow, direction)) {
            settingsReader.getFocusOutsideTable(direction);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            const row = e.currentTarget;
            handleTab(e, row);
        }
    };

    const nameIdentifier = rowChoiceIdentifier;
    const number = scorableItemNumberingService.getInteractionChoiceNumber(
        questionModel.identifier,
        modelIdentifier,
        nameIdentifier
    );

    const isFlaggingEnabled = flaggedItems?.includes(`${questionModel.identifier}-${number}`);

    // Check if v3 accessibility is enabled for row header tabIndex
    const rowHeaderTabIndex = matchInteractionA11yService.getRowHeaderTabIndex(undefined, 0);
    const questionIdAttributes = matchInteractionA11yService.getQuestionIdAttributes(questionModel.identifier);
    const rowHeaderScope = matchInteractionA11yService.getRowHeaderScope();

    // Check if v4 accessibility is enabled - if so, remove aria-labelledby from tr
    const isV4Supported = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v4);

    return (
        <tr
            key={`row_${rowChoiceIdentifier}`}
            aria-labelledby={isV4Supported ? undefined : `rowChoice_${rowChoiceIdentifier}`}
            onKeyDown={handleKeyDown}
        >
            <th
                id={`rowChoice_${rowChoiceIdentifier}`}
                scope={rowHeaderScope}
                tabIndex={rowHeaderTabIndex}
                {...questionIdAttributes}
            >
                <div className={styles.headerCellWrapper}>
                    { !isInverted &&
                        <InteractionNumber
                            modelIdentifier={modelIdentifier}
                            questionContentRevisionId={questionModel.contentRevisionId}
                            flaggedItems={flaggedItems}
                            choiceIdentifier={rowChoiceIdentifier}
                        />
                    }

                    <div className="inline" dangerouslySetInnerHTML={{ __html: rowChoice.content }} />
                </div>
            </th>

            { matchColumns.map((columnChoice) => {
                const columnChoiceIdentifier = columnChoice.identifier;
                return (<AlternativeCell
                    columnChoiceIdentifier={columnChoiceIdentifier}
                    correctResponses={correctResponses}
                    handleUserResponse={handleUserResponse}
                    modelIdentifier={modelIdentifier}
                    questionId={questionModel.identifier}
                    isInverted={isInverted}
                    isReadOnly={isReadOnly}
                    isSelected={userResponse.includes(`${rowChoiceIdentifier} ${columnChoiceIdentifier}`)}
                    key={columnChoiceIdentifier}
                    rowChoiceIdentifier={rowChoiceIdentifier}
                    flaggedItems={flaggedItems}
                />);
            })}
        </tr>
    );
};

MatchInteractionRow.propTypes = {
    correctResponses: PropTypes.array,
    flaggedItems: PropTypes.array,
    handleUserResponse: PropTypes.func.isRequired,
    isInverted: PropTypes.bool.isRequired,
    isReadOnly: PropTypes.bool,
    matchColumns: PropTypes.array.isRequired,
    modelIdentifier: PropTypes.string.isRequired,
    questionModel: PropTypes.object.isRequired,
    rowChoice: PropTypes.object.isRequired,
    userResponse: PropTypes.array.isRequired,
};

export default MatchInteractionRow;
